// The invoice report's page arithmetic: how many cover pages the contents
// listing needs, and which absolute page each invoice starts on.
//
// PURE, AND ITS OWN MODULE, because this is the part that has to be right. A
// cover that spills to a second page shifts every offset below it, so the
// listing cannot be laid out until its own length is known — and its length
// depends on the rows it lists. Getting that wrong produces a report whose
// table of contents is off by one for every entry, which is worse than no
// contents at all and invisible until someone turns to page 14.
//
// `reportPdf.ts` draws using these numbers and the row heights this module
// exports, so the drawing and the arithmetic cannot disagree.

import type { ReportInvoiceRow } from '../api/types'

/** A report row with the page count its source pages actually came to. */
export interface CountedRow {
  row: ReportInvoiceRow
  /**
   * Pages this invoice contributes. **Zero** for an invoice with no page span
   * (manual entry, a row from before splitting) or whose upload would not
   * parse — it is still LISTED, with no page number.
   */
  pageCount: number
}

export interface LaidOutRow extends CountedRow {
  /** Absolute page in the assembled file, 1-based and including the cover.
      Null when `pageCount` is 0. */
  firstPage: number | null
  /** How tall this row is on the cover, in points. Drawing reads this. */
  height: number
}

interface ReportLayout {
  coverPageCount: number
  rows: LaidOutRow[]
  /** Cover pages plus every invoice's pages. */
  totalPages: number
  /** Rows listed with no page number, so the cover can say how many. */
  missingPageCount: number
}

// ---- The cover page's geometry -------------------------------------------
//
// Letter portrait at 72 points per inch, half-inch margins. These are exported
// because `reportPdf.ts` draws to them; changing one changes the layout, so
// they live here with the arithmetic that consumes them rather than beside the
// drawing code where the two could drift.

export const PAGE_WIDTH = 612
export const PAGE_HEIGHT = 792
export const MARGIN = 36

/** Body text on the cover. 8pt fits the eight columns at Letter width. */
export const ROW_FONT_SIZE = 8
export const ROW_LINE_HEIGHT = 10
/**
 * Padding above and below a row's text block (owner, 2026-09-10: *"a bit more
 * spacing between your table rows"*).
 *
 * Raised from 3. It is the ONE number that sets row spacing, and `rowHeight`
 * derives from it, so the page arithmetic follows automatically — a taller row
 * simply means fewer rows per cover page, which `layOutReport` already computes
 * rather than assumes.
 */
export const ROW_PADDING = 6

/**
 * Vertical space the FIRST cover page gives up before the table starts.
 *
 * MUST MATCH WHAT `reportPdf.ts` ACTUALLY DRAWS, term for term, or the first
 * page either overflows its own listing or leaves a band of white above it. The
 * terms are: the brand lockup, the ONE run line, the criteria panel (padding,
 * its heading, and one line per criterion), the gap, the omitted-invoices
 * warning panel when there is one (D-354, built the same way, with its own
 * gap), and the column header.
 *
 * Criteria are variable, so this is a function of how many lines they came to.
 */
export function firstPageHeaderHeight(
  criteriaLineCount: number,
  runLineCount = 1,
  warningLineCount = 0,
): number {
  const lockup = BRAND_BLOCK_HEIGHT + 6
  // TWO lines when the report is being downloaded again (owner, 2026-09-14):
  // the original run, then the download. The arithmetic has to know, or the
  // first invoice row is laid out over the second line.
  const runLine = Math.max(runLineCount, 1) * ROW_LINE_HEIGHT
  const panel =
    CRITERIA_PANEL_PADDING * 2 +
    ROW_LINE_HEIGHT +
    Math.max(criteriaLineCount, 1) * ROW_LINE_HEIGHT
  const gap = 12
  const warning =
    warningLineCount > 0
      ? CRITERIA_PANEL_PADDING * 2 + ROW_LINE_HEIGHT + warningLineCount * ROW_LINE_HEIGHT + 12
      : 0
  return lockup + runLine + gap + panel + 12 + warning + COLUMN_HEADER_HEIGHT + 3
}

/**
 * The omitted-invoices warning under Criteria (owner, 2026-10-13 — D-354): how
 * many invoices in the runner's own Reporting companies the report left out
 * because they wait at Import Control on a company flag. Empty when none were,
 * and then no panel is drawn. One sentence a line, so neither is cut short.
 */
export function omittedCompanyFlagLines(count: number): string[] {
  if (count <= 0) return []
  return count === 1
    ? [
        '1 invoice was omitted due to outstanding flags related to its Company value.',
        'When those flags are resolved at Import Control, the invoice will become available for Analytics.',
      ]
    : [
        `${count} invoices were omitted due to outstanding flags related to their Company value.`,
        'When those flags are resolved at Import Control, the invoices will become available for Analytics.',
      ]
}

/** The brand lockup's own height — the mark, with the title beside it. */
export const BRAND_BLOCK_HEIGHT = 30

/** Inner padding of the criteria panel, top and bottom. */
export const CRITERIA_PANEL_PADDING = 8

/**
 * The column header band, and every cover page after the first repeats it.
 *
 * TALLER THAN THE TEXT, on purpose: the labels are centred inside the band and
 * the rule sits under the band, not two points under the baseline. The first
 * version drew the rule through the labels' descenders (owner: *"your headers
 * are overlapping with the underline"*).
 */
export const COLUMN_HEADER_HEIGHT = 18

/** Space reserved at the foot of every cover page for `Page N of M`. */
export const FOOTER_HEIGHT = 18

/**
 * The totals line, which must sit on a cover page and never be orphaned onto a
 * page of its own after the last row.
 *
 * TWO line heights: the rule and the total itself, then the one note that can
 * follow it — invoices listed without source pages. A second note briefly
 * existed here for invoices a re-download could not reach; it was dropped on
 * 2026-09-14 once every role that can run a report reads every company (D-98),
 * which makes that state unreachable.
 */
export const TOTALS_HEIGHT = 2 * ROW_LINE_HEIGHT

/**
 * How many text lines a row occupies. Driven by the general-ledger codes, which
 * are the one variable-length cell: an invoice coded across four codes needs
 * four lines — one per code, each with its own subtotal (owner, 2026-09-10) —
 * where a single-code one needs one.
 *
 * Everything else in the row is one line by construction: a vendor or company
 * name too long for its column is truncated when drawn, not wrapped, because a
 * contents listing that reflows is a contents listing whose page numbers move.
 */
export function rowLineCount(row: ReportInvoiceRow): number {
  return Math.max(1, row.code_subtotals.length)
}

export function rowHeight(row: ReportInvoiceRow): number {
  return rowLineCount(row) * ROW_LINE_HEIGHT + ROW_PADDING * 2
}

/**
 * Lay out the report.
 *
 * Two passes, and the order is the whole point: the cover's own length is
 * settled FIRST, from the rows and their heights, and only then are the page
 * offsets computed — because every offset includes the cover.
 *
 * Called AFTER the source pages have been fetched, so `pageCount` reflects what
 * actually reached the file rather than what the page spans promised
 * (`docs/api.md` §16a). An invoice whose packet would not parse arrives here
 * with `pageCount: 0` and is listed without a page number.
 */
export function layOutReport(
  counted: readonly CountedRow[],
  criteriaLineCount: number,
  runLineCount = 1,
  warningLineCount = 0,
): ReportLayout {
  const heights = counted.map(({ row }) => rowHeight(row))

  // --- pass one: how many cover pages does the listing need? ---------------
  const usable = (pageIndex: number): number =>
    PAGE_HEIGHT -
    MARGIN * 2 -
    FOOTER_HEIGHT -
    (pageIndex === 0
      ? firstPageHeaderHeight(criteriaLineCount, runLineCount, warningLineCount)
      : COLUMN_HEADER_HEIGHT)

  let coverPageCount = 1
  let used = 0
  for (const height of heights) {
    if (used + height > usable(coverPageCount - 1)) {
      coverPageCount += 1
      used = 0
    }
    used += height
  }
  // The totals line must not be orphaned. If it does not fit under the last
  // row, it takes a page of its own — and that page is a cover page, so it
  // counts here, before any offset is computed.
  if (used + TOTALS_HEIGHT > usable(coverPageCount - 1)) coverPageCount += 1

  // --- pass two: the offsets, now that the cover's length is known ---------
  let nextPage = coverPageCount + 1
  const rows: LaidOutRow[] = counted.map((entry, index) => {
    if (entry.pageCount <= 0) {
      return { ...entry, firstPage: null, height: heights[index]! }
    }
    const firstPage = nextPage
    nextPage += entry.pageCount
    return { ...entry, firstPage, height: heights[index]! }
  })

  return {
    coverPageCount,
    rows,
    totalPages: nextPage - 1,
    missingPageCount: counted.filter((entry) => entry.pageCount <= 0).length,
  }
}
